import { useState, useEffect, useRef } from 'react';
import { backButton, closingBehavior } from '@telegram-apps/sdk-react';
import { TabId } from './domain/types';
import { useTapUser, useTapSession } from './hooks';
import { pushPageView } from './lib/gtm';
import { TapScreen } from './components/TapScreen';
import { ProgressScreen } from './components/ProgressScreen';
import { InviteScreen } from './components/InviteScreen';
import { BottomNav } from './components/BottomNav';
import { SessionEndModal } from './components/SessionEndModal';
import { EndSessionResponse } from './domain/types';

export function App() {
  const [activeTab, setActiveTab] = useState<TabId>('tap');
  const [sessionResult, setSessionResult] = useState<EndSessionResponse | null>(null);

  const [userState, userActions] = useTapUser();
  const [sessionState, sessionActions] = useTapSession((result) => {
    setSessionResult(result);
    userActions.refreshAll();
  });

  // BackButton handling for non-home screens
  useEffect(() => {
    try {
      if (activeTab !== 'tap') {
        backButton.show();
        const handleBack = () => setActiveTab('tap');
        backButton.onClick(handleBack);
        return () => {
          backButton.hide();
          backButton.offClick(handleBack);
        };
      } else {
        backButton.hide();
      }
    } catch {
      // BackButton not available outside Telegram
    }
  }, [activeTab]);

  // Enable closing confirmation during active session
  useEffect(() => {
    try {
      if (sessionState.isActive) {
        closingBehavior.enableConfirmation();
      } else {
        closingBehavior.disableConfirmation();
      }
    } catch {
      // ClosingBehavior not available outside Telegram
    }
  }, [sessionState.isActive]);

  // Track virtual page views on tab change (skip initial render)
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    pushPageView(activeTab);
  }, [activeTab]);

  const handleCloseModal = () => {
    setSessionResult(null);
    sessionActions.reset();
  };

  // Loading state
  if (userState.isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
      </div>
    );
  }

  // Error state
  if (userState.error && !userState.user) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-red-400">{userState.error}</p>
        <button
          onClick={() => userActions.refreshAll()}
          className="rounded-lg bg-primary-500 px-4 py-2 font-medium text-white"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Main content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'tap' && (
          <TapScreen
            user={userState.user}
            status={userState.status}
            sessionState={sessionState}
            sessionActions={sessionActions}
            onRefreshStatus={userActions.refreshStatus}
          />
        )}
        {activeTab === 'progress' && (
          <ProgressScreen user={userState.user} />
        )}
        {activeTab === 'invite' && (
          <InviteScreen user={userState.user} />
        )}
      </div>

      {/* Bottom navigation - visible but dimmed during session per spec */}
      <BottomNav
        activeTab={activeTab}
        onTabChange={setActiveTab}
        dimmed={sessionState.isActive}
      />

      {/* Session end modal */}
      {sessionResult && (
        <SessionEndModal result={sessionResult} onClose={handleCloseModal} />
      )}
    </div>
  );
}
